import React, { useEffect, useState } from 'react';
import { FollowUp, settings, shipment } from '../../../../models/entities/shipment/shipment';
import Parties from './Parties';
import Events from './Events/EventsTab';
import Documents from './Documents/DocumentsTab';
import InternalDocuments from './Documents/InternalDocumentsTab';
import TabsContainer from '../../../shared/Tabs/TabsContainer';
import Tab from '../../../shared/Tabs/Tab';
import History from '../../commonSubEntities/history/HistoryTab';
import ShipmentOverviewTab from './OverviewTab';
import { useLocation } from 'react-router-dom';
import { settings as companySettings } from '../../../../models/entities/businessPartner/businessPartner';
import ShipmentExpressEvents from './Events/express/ExpressEventsTab';
import claimsType from '../../../../models/entities/claimsType';
import Quotations from '../../bids/singleBid/sections/Quotations';
import { bid } from '../../../../models/entities/bid/bid';
import { featureFlagsModel } from '../../../../models/entities/businessPartner/settings';
import { drawers } from '../../../../state/ducks/header/header';
import { claimsDomainsType } from '../../../../userClaims';
import ShipmentItemsTab from './ShipmentItems/ShipmentItemsTab';
import Accounting from './Accounting';
import FollowUpTab from './FollowUpTab';
import { userDetails } from '../../../../state/ducks/users/users';
import { userSettings } from '../../../../models/entities/user';

type BaseProps = {
    shipment: shipment;
    settings: settings;
    isAllowedShipment?: boolean;
    localization?: any;
    companySettings?: companySettings;
    printMode?: boolean;
    /** Applied in Accounting tab when cargo owner has a profit surcharge configured. */
    cargoOwnerProfitSurchargePercent?: number | null;
    /** Cargo owner base currency for converting line total cost in Accounting. */
    cargoOwnerBaseCurrency?: string | null;
    cargoOwnerVat?: number | null;
};
type Props = BaseProps & {
    isLoadingShipment?: boolean;
    isFreelancerOrForwarder?: boolean;
    isMobile?: boolean;
    tab?: string;
    printMode?: boolean;
    getUserClaims: (domain: claimsDomainsType) => claimsType;
    dismissRequiredDoc?: (shipmentId: string, docType: string) => string;
    updateBid?: (id: string, bid: bid, shipment: shipment, publish: boolean) => Promise<boolean>;
    createBid?: (shipment: shipment, bid: bid, publish: boolean) => Promise<string>;
    updateShipmentAccountingState: (shipmentId: string, shipmentAccountingState: string) => Promise<void>;
    startBid?: (id: string, shipmentId: string, shipment: shipment) => Promise<void>;
    featureFlags?: featureFlagsModel;
    setDrawerOpen: (open: drawers, params?: any) => void;
    addNewFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    updateFollowUpCompletionStatus: (shipmentId: string, followUpId: number, isCompleted: boolean) => Promise<void>;
    updateFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    deleteFollowUp: (shipmentId: string, followUpId: number) => Promise<void>;
    users: userDetails[];
    followupGridSettings?: userSettings;
    currentUserId: string;
    onRefreshShipment?: () => void;
};

const ShipmentTabs = ({
    shipment,
    isLoadingShipment,
    localization,
    settings,
    companySettings,
    isMobile,
    dismissRequiredDoc,
    getUserClaims,
    updateBid,
    createBid,
    startBid,
    featureFlags,
    setDrawerOpen,
    updateShipmentAccountingState,
    isAllowedShipment,
    isFreelancerOrForwarder,
    addNewFollowUp,
    updateFollowUpCompletionStatus,
    deleteFollowUp,
    currentUserId,
    users,
    updateFollowUp,
    followupGridSettings,
    onRefreshShipment,
    cargoOwnerProfitSurchargePercent,
    cargoOwnerBaseCurrency,
    cargoOwnerVat
}: Props) => {
    const location = useLocation();

    const tabParam = new URLSearchParams(location.search).get('tab');

    const compareCaseInsensitive = (first: string, second: string) => {
        return first.toUpperCase() === second.toUpperCase();
    };

    /** Match tab ids ignoring case/spaces so ?tab=Internal%20Documents works. */
    const compareTabId = (first: string, second: string) => {
        return first.replace(/\s+/g, '').toUpperCase() === second.replace(/\s+/g, '').toUpperCase();
    };

    const getActiveTab = () => {
        if (tabParam) {
            const tabs = ['Parties', 'Events', 'Documents', 'InternalDocuments', 'History', 'Quotations', 'Accounting', 'FollowUp', 'Items'];
            const tabsMobile = tabs.concat('Overview');

            // EXPRESS uses ExpressEvents as the Events tab key
            if (compareCaseInsensitive(tabParam, 'Events') && shipment.typeCode === 'EXPRESS') {
                return 'ExpressEvents';
            }
            if (compareCaseInsensitive(tabParam, 'ExpressEvents')) {
                return 'ExpressEvents';
            }

            const matchedTab = (isMobile ? tabsMobile : tabs).find((tab) => compareTabId(tab, tabParam));
            return matchedTab ? matchedTab : isMobile ? 'Overview' : shipment.typeCode === 'EXPRESS' ? 'ExpressEvents' : 'Parties';
        }
        return isMobile ? 'Overview' : shipment.typeCode !== 'EXPRESS' ? 'Parties' : 'ExpressEvents';
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());

    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [location.search, location.pathname, shipment.typeCode, isMobile]);

    const mobileToUpper = (title: string) => {
        return isMobile ? title.toUpperCase() : title;
    };

    // If claims/flags hide the active tab, fall back so TabsContainer always has content.
    const resolvedActiveTab = (() => {
        if (compareCaseInsensitive(activeTab, 'ExpressEvents') && shipment.typeCode === 'EXPRESS') return 'ExpressEvents';
        if (compareCaseInsensitive(activeTab, 'Overview') && isMobile) return 'Overview';
        if (compareCaseInsensitive(activeTab, 'Parties')) return 'Parties';
        if (compareCaseInsensitive(activeTab, 'Items') && !!shipment.items?.length) return 'Items';
        if (compareCaseInsensitive(activeTab, 'Quotations') && featureFlags?.BIDS && isAllowedShipment) return 'Quotations';
        if (compareCaseInsensitive(activeTab, 'Events') && getUserClaims('shipment').view_event && shipment.typeCode !== 'EXPRESS') return 'Events';
        if (compareCaseInsensitive(activeTab, 'Documents') && getUserClaims('shipment').view_document) return 'Documents';
        if (compareCaseInsensitive(activeTab, 'InternalDocuments') && getUserClaims('shipment').view_document) return 'InternalDocuments';
        if (compareCaseInsensitive(activeTab, 'History') && !featureFlags?.UNDER_FORWARDER && isAllowedShipment) return 'History';
        if (compareCaseInsensitive(activeTab, 'Accounting') && isFreelancerOrForwarder && isAllowedShipment) return 'Accounting';
        if (compareCaseInsensitive(activeTab, 'FollowUp')) return 'FollowUp';
        return isMobile ? 'Overview' : shipment.typeCode === 'EXPRESS' ? 'ExpressEvents' : 'Parties';
    })();

    const isTabActive = (tab: string) => compareCaseInsensitive(resolvedActiveTab, tab);

    return (
        <TabsContainer scrollToTabs={!!tabParam && !isLoadingShipment}>
            {isMobile && (
                <Tab title={mobileToUpper('Overview')} onClick={() => setActiveTab('Overview')} isActive={isTabActive('Overview')}>
                    <ShipmentOverviewTab shipment={shipment} localization={localization} settings={settings} />
                </Tab>
            )}
            {shipment.typeCode === 'EXPRESS' && (
                <Tab title={mobileToUpper('Events')} onClick={() => setActiveTab('ExpressEvents')} isActive={isTabActive('ExpressEvents')}>
                    <ShipmentExpressEvents
                        events={shipment.events}
                        settings={settings}
                        localization={localization.events}
                        userShipmentEventsClaims={getUserClaims('shipment')}
                        shipment={shipment}
                        users={users}
                        addNewFollowUp={addNewFollowUp}
                        currentUserId={currentUserId}
                    />
                </Tab>
            )}
            <Tab title={mobileToUpper('Parties')} onClick={() => setActiveTab('Parties')} isActive={isTabActive('Parties')}>
                <Parties shipment={shipment} localization={localization} settings={companySettings} featureFlags={featureFlags} />
            </Tab>
            {!!shipment.items?.length && (
                <Tab title={mobileToUpper('Items')} onClick={() => setActiveTab('Items')} isActive={isTabActive('Items')}>
                    <ShipmentItemsTab
                        shipment={shipment}
                        localization={localization.items}
                        isAllowedShipment={isAllowedShipment}
                        featureFlags={featureFlags}
                        isFreelancerOrForwarder={isFreelancerOrForwarder}
                    />
                </Tab>
            )}
            {featureFlags?.BIDS && isAllowedShipment && (
                <Tab title="Quotations" onClick={() => setActiveTab('Quotations')} isActive={isTabActive('Quotations')}>
                    <Quotations
                        featureFlags={featureFlags}
                        localization={localization.bid}
                        updateBid={updateBid}
                        startBid={startBid}
                        createBid={createBid}
                        shipment={shipment}
                        setDrawerOpen={setDrawerOpen}
                    />
                </Tab>
            )}
            {getUserClaims('shipment').view_event && shipment.typeCode !== 'EXPRESS' && (
                <Tab title={mobileToUpper('Events')} onClick={() => setActiveTab('Events')} isActive={isTabActive('Events')}>
                    <Events
                        events={shipment.events}
                        localization={localization}
                        settings={settings}
                        userShipmentEventsClaims={getUserClaims('shipment')}
                        featureFlags={featureFlags}
                        shipment={shipment}
                        users={users}
                        addNewFollowUp={addNewFollowUp}
                        currentUserId={currentUserId}
                    />
                </Tab>
            )}
            {getUserClaims('shipment').view_document && (
                <Tab title={mobileToUpper('Documents')} onClick={() => setActiveTab('Documents')} isActive={isTabActive('Documents')}>
                    <Documents
                        documents={shipment.documents?.documents}
                        requiredDocs={shipment.documents?.requiredDocs}
                        localization={localization}
                        settings={settings}
                        dismissRequiredDoc={dismissRequiredDoc}
                        userShipmentDocsClaims={getUserClaims('shipment')}
                        shipmentId={shipment.id}
                    />
                </Tab>
            )}
            {getUserClaims('shipment').view_document && (
                <Tab
                    title={mobileToUpper('Internal Documents')}
                    onClick={() => setActiveTab('InternalDocuments')}
                    isActive={isTabActive('InternalDocuments')}
                >
                    <InternalDocuments
                        documents={shipment.internalDocuments?.documents}
                        requiredDocs={shipment.internalDocuments?.requiredDocs}
                        localization={localization}
                        settings={settings}
                        dismissRequiredDoc={dismissRequiredDoc}
                        userShipmentDocsClaims={getUserClaims('shipment')}
                        shipmentId={shipment.id}
                    />
                </Tab>
            )}
            {/* <Tab title="References" onClick={() => setActiveTab('References')} isActive={activeTab === 'References'}>
                <div>References content</div>
            </Tab> */}
            {!featureFlags?.UNDER_FORWARDER && isAllowedShipment && (
                <Tab title={mobileToUpper('History')} onClick={() => setActiveTab('History')} isActive={isTabActive('History')}>
                    <History
                        history={shipment.history}
                        localization={localization}
                        excludedFields={
                            isFreelancerOrForwarder ? undefined : ['buyingRate', 'buyingRateCurrency', 'sellingRate', 'sellingRateCurrency']
                        }
                    />
                </Tab>
            )}
            {isFreelancerOrForwarder && isAllowedShipment && (
                <Tab title={mobileToUpper('Accounting')} onClick={() => setActiveTab('Accounting')} isActive={isTabActive('Accounting')}>
                    <Accounting
                        shipment={shipment}
                        billingLines={shipment.billingLines}
                        updateShipmentAccountingState={updateShipmentAccountingState}
                        localization={localization}
                        settings={settings}
                        featureFlags={featureFlags}
                        cargoOwnerProfitSurchargePercent={cargoOwnerProfitSurchargePercent}
                        cargoOwnerBaseCurrency={cargoOwnerBaseCurrency}
                        cargoOwnerVat={cargoOwnerVat}
                    />
                </Tab>
            )}

            <Tab title={mobileToUpper('Follow up')} onClick={() => setActiveTab('FollowUp')} isActive={isTabActive('FollowUp')}>
                <FollowUpTab
                    shipment={shipment}
                    users={users}
                    addNewFollowUp={addNewFollowUp}
                    updateFollowUpCompletionStatus={updateFollowUpCompletionStatus}
                    deleteFollowUp={deleteFollowUp}
                    currentUserId={currentUserId}
                    updateFollowUp={updateFollowUp}
                    followupGridSettings={followupGridSettings}
                />
            </Tab>
        </TabsContainer>
    );
};

export const ShipmentTabsPrintMode = ({ shipment, localization, settings, companySettings, printMode, isAllowedShipment }: BaseProps) => {
    return (
        <div style={{ width: '100%' }}>
            <Parties shipment={shipment} localization={localization} settings={companySettings} printMode={printMode} />
        </div>
    );
};

export default ShipmentTabs;
